<template>
  <div class="pixso-page collaborative-inspection" :class="{ 'collaborative-inspection--en': isEnglish }">
    <main class="collaborative-inspection__main">
      <section class="collaborative-inspection__hero" aria-labelledby="collaborative-inspection-title">
        <span class="collaborative-inspection__hero-mask" aria-hidden="true"></span>
        <div class="collaborative-inspection__hero-copy">
          <p class="pixso-eyebrow">{{ t('CollaborativeInspection.hero.eyebrow') }}</p>
          <h1 id="collaborative-inspection-title" class="collaborative-inspection__title pixso-heading-xl">
            {{ t('CollaborativeInspection.hero.title') }}
          </h1>
          <p class="collaborative-inspection__desc pixso-body-lg">
            {{ t('CollaborativeInspection.hero.description') }}
          </p>
        </div>
      </section>

      <section id="collaborative-solutions" class="collaborative-inspection__solutions pixso-stage" :aria-label="t('CollaborativeInspection.cards.ariaLabel')">
        <div class="collaborative-inspection__grid">
          <router-link
            v-for="item in inspectionSolutions"
            :id="item.id"
            :key="item.title"
            class="collaborative-inspection__card"
            :to="item.to"
            :aria-label="t('CollaborativeInspection.cards.moreAria', { title: item.title, description: item.description })"
          >
            <img class="collaborative-inspection__card-image" :src="item.image" alt="" aria-hidden="true" :style="{ objectPosition: item.imagePosition || 'center' }" />
            <span class="collaborative-inspection__card-mask" aria-hidden="true"></span>
            <span class="collaborative-inspection__card-copy">
              <span class="collaborative-inspection__card-title">{{ item.title }}</span>
              <span class="collaborative-inspection__card-more">{{ t('CollaborativeInspection.cards.more') }} &gt;</span>
            </span>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import securityImage from '@/assets/images/pixso-collaborative-inspection/card-security-source.jpg'
import industrialImage from '@/assets/images/pixso-collaborative-inspection/card-industrial-source.jpg'
import tunnelImage from '@/assets/images/pixso-collaborative-inspection/card-tunnel-source.jpg'

const { locale, t } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const inspectionSolutions = computed(() => [
  {
    id: 'security-inspection',
    title: t('CollaborativeInspection.cards.securityInspection.title'),
    description: t('CollaborativeInspection.cards.securityInspection.description'),
    image: securityImage,
    to: '/solution/public-security-inspection'
  },
  {
    id: 'industrial-inspection',
    title: t('CollaborativeInspection.cards.industrialInspection.title'),
    description: t('CollaborativeInspection.cards.industrialInspection.description'),
    image: industrialImage,
    imagePosition: '70% center',
    to: '/solution/industrial-inspection'
  },
  {
    id: 'tunnel-inspection',
    title: t('CollaborativeInspection.cards.tunnelInspection.title'),
    description: t('CollaborativeInspection.cards.tunnelInspection.description'),
    image: tunnelImage,
    to: '/solution/tunnel-corridor-inspection'
  }
])
</script>

<style scoped>
.collaborative-inspection {
  --collaborative-card-ratio: 884 / 811;
  --collaborative-solutions-top: var(--pixso-fs-96);
  --collaborative-solutions-bottom: var(--pixso-section-gap-xl);
  --collaborative-card-row-gap: var(--pixso-fs-64);
  --collaborative-card-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0) 48%, rgba(0, 0, 0, 0.64) 100%);
  --collaborative-card-hover-y: -4px;
  --collaborative-solutions-bottom-tablet: 220px;
}

.collaborative-inspection__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.collaborative-inspection__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.22) 0%, rgba(0, 0, 0, 0.12) 58%, var(--pixso-color-bg) 100%),
    url("@/assets/images/pixso-collaborative-inspection/hero.jpg") center / cover no-repeat;
}

.collaborative-inspection__hero-mask {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0.1) 56%, var(--pixso-color-bg) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 45%, rgba(0, 0, 0, 0.3) 100%);
}

.collaborative-inspection__hero-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--pixso-container));
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-md);
  padding: 0 calc(var(--pixso-page-padding) + var(--pixso-gap-md));
  text-align: center;
  transform: translateY(-14px);
}

.collaborative-inspection__hero-copy p,
.collaborative-inspection__title {
  margin: 0;
}

.collaborative-inspection__title,
.collaborative-inspection__desc {
  color: var(--pixso-color-text);
}

.collaborative-inspection__desc {
  max-width: 1008px;
}

.collaborative-inspection__solutions {
  padding: var(--collaborative-solutions-top) var(--pixso-page-padding) var(--collaborative-solutions-bottom);
}

.collaborative-inspection__grid {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--collaborative-card-row-gap) var(--pixso-gap-md);
}

.collaborative-inspection__card {
  position: relative;
  display: block;
  aspect-ratio: var(--collaborative-card-ratio);
  overflow: hidden;
  border-radius: var(--pixso-radius-lg);
  color: var(--pixso-color-text);
  cursor: pointer;
  text-decoration: none;
  background: var(--pixso-color-surface);
  transition:
    transform 0.48s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.48s cubic-bezier(0.22, 1, 0.36, 1);
}

.collaborative-inspection__card:hover {
  transform: translateY(var(--collaborative-card-hover-y));
  filter: brightness(1.06);
}

.collaborative-inspection__card:focus-visible {
  outline: 2px solid var(--pixso-color-primary);
  outline-offset: 4px;
}

.collaborative-inspection__card-image,
.collaborative-inspection__card-mask {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.collaborative-inspection__card-image {
  object-fit: cover;
}

.collaborative-inspection__card-mask {
  background: var(--collaborative-card-overlay);
}

.collaborative-inspection__card-copy {
  position: absolute;
  left: var(--pixso-gap-md);
  right: var(--pixso-gap-md);
  bottom: var(--pixso-gap-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  text-align: center;
}

.collaborative-inspection__card-title {
  font-size: var(--pixso-fs-36);
  font-weight: 700;
  line-height: var(--pixso-lh-36);
}

.collaborative-inspection__card-more {
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  font-weight: 700;
  line-height: var(--pixso-lh-16);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.collaborative-inspection--en .collaborative-inspection__card-more {
  font-weight: 400;
}

@media (prefers-reduced-motion: reduce) {
  .collaborative-inspection__card {
    transition: none;
  }

  .collaborative-inspection__card:hover {
    transform: none;
    filter: none;
  }
}

@media (max-width: 1200px) {
  .collaborative-inspection__hero {
    height: var(--pixso-hero-height);
    min-height: var(--pixso-hero-min);
  }

  .collaborative-inspection__hero-copy {
    padding: 0;
  }

  .collaborative-inspection__solutions {
    padding-top: calc(var(--pixso-gap-lg) + var(--pixso-gap-md));
    padding-bottom: var(--collaborative-solutions-bottom-tablet);
  }
}

@media (max-width: 768px) {
  .collaborative-inspection__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .collaborative-inspection__hero {
    background-position: 58% center;
  }

  .collaborative-inspection__hero-copy {
    gap: var(--pixso-gap-sm);
  }

  .collaborative-inspection__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .collaborative-inspection__desc {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }

  .collaborative-inspection__solutions {
    padding: var(--pixso-gap-lg) var(--pixso-page-padding) var(--pixso-section-gap-xl);
  }

  .collaborative-inspection__grid {
    grid-template-columns: 1fr;
    gap: var(--pixso-gap-sm);
  }

  .collaborative-inspection__card {
    border-radius: var(--pixso-radius-md);
  }

  .collaborative-inspection__card-copy {
    bottom: var(--pixso-gap-md);
  }

  .collaborative-inspection__card-title {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .collaborative-inspection__card-more {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }
}
</style>
